import React, { useEffect, useState } from 'react'
import { api } from '../lib/api'
import ConfigPageLayout from '../components/ConfigPageLayout'

export default function WordPressConfigPage() {
  const [wpBaseUrl, setWpBaseUrl] = useState('')
  const [wpAppUser, setWpAppUser] = useState('')
  const [wpAppPassword, setWpAppPassword] = useState('')
  const [wpSubjectOwnerUserId, setWpOwner] = useState<number | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [testMessage, setTestMessage] = useState<string | null>(null)
  const [testError, setTestError] = useState<string | null>(null)
  const [users, setUsers] = useState<Array<{id:number,email:string,is_superuser:boolean}>>([])
  const [me, setMe] = useState<{id:number,email:string,is_superuser:boolean} | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/settings/me')
        setWpBaseUrl(data.wp_base_url || '')
        setWpAppUser(data.wp_app_user || '')
        setWpAppPassword(data.wp_app_password || '')
        if (data.wp_subject_owner_user_id != null) setWpOwner(Number(data.wp_subject_owner_user_id))
      } catch (e: any) {
        setError(e?.response?.data?.detail || 'Erreur chargement paramètres')
      }
      try {
        const { data } = await api.get('/fapi/users/me')
        setMe(data)
        if (data?.is_superuser) {
          const res = await api.get('/fapi/users/admin/all')
          setUsers(res.data)
        } else {
          setUsers([data])
        }
      } catch {}
      setLoading(false)
    })()
  }, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setMessage(null)
    setError(null)
    setTestMessage(null)
    setTestError(null)
    try {
      await api.put('/settings/me', { 
        wp_base_url: wpBaseUrl, 
        wp_app_user: wpAppUser, 
        wp_app_password: wpAppPassword, 
        wp_subject_owner_user_id: wpSubjectOwnerUserId ?? (me?.id ?? null) 
      })
      setMessage('Paramètres WordPress sauvegardés avec succès ✅')
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur sauvegarde paramètres')
    } finally {
      setSaving(false)
    }
  }

  const testCourses = async () => {
    setTestMessage(null)
    setTestError(null)
    try {
      const { data } = await api.get('/wp/courses')
      setTestMessage(`✅ Connexion réussie ! ${data.length} formation(s) détectée(s)`)
    } catch (e: any) {
      setTestError(e?.response?.data?.detail || 'Erreur récupération formations')
    }
  }

  if (loading) {
    return (
      <ConfigPageLayout title="WORDPRESS" description="Configuration de la connexion WordPress et LearnDash" backTo="/config">
        <div className="flex items-center justify-center py-12">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
        </div>
      </ConfigPageLayout>
    )
  }

  return (
    <ConfigPageLayout title="WORDPRESS" description="Configuration de la connexion WordPress et LearnDash pour synchroniser vos formations" backTo="/config">
      <form onSubmit={submit} className="space-y-6">
        <section className="space-y-6 rounded-3xl border border-blue-100/80 bg-white/90 p-8 shadow-xl shadow-blue-500/10 dark:border-blue-900/50 dark:bg-zinc-950/90">
          <div className="flex items-center gap-3 pb-4 border-b border-blue-200/50 dark:border-blue-800/50">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100 dark:bg-blue-900/40">
              <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.158 12.786l-2.698 7.84c.806.236 1.657.365 2.54.365 1.047 0 2.051-.18 2.986-.51-.024-.037-.046-.078-.065-.123l-2.763-7.572zm-5.316 3.713l-3.106-8.98c-.538 1.003-.838 2.15-.838 3.365 0 2.469 1.266 4.643 3.18 5.918-.02-.103-.038-.206-.054-.31l-.182-.993zm9.928-8.986c.54-.028.998-.085.998-.085.47-.056.415-.75-.056-.724 0 0-1.414.112-2.327.112-.858 0-2.3-.112-2.3-.112-.47-.026-.526.697-.055.724 0 0 .43.057.884.085l1.313 3.598-1.845 5.536-3.073-9.134c.54-.028.998-.085.998-.085.47-.056.415-.75-.056-.724 0 0-1.414.112-2.327.112-.164 0-.358-.004-.564-.01C6.78 3.924 9.24 2.571 12 2.571c2.072 0 3.958.79 5.375 2.086-.034-.002-.068-.007-.103-.007-.858 0-1.467.75-1.467 1.556 0 .723.415 1.333.858 2.056.332.585.72 1.333.72 2.417 0 .75-.288 1.62-.666 2.83l-.872 2.913-3.16-9.395zM12 22c-5.523 0-10-4.477-10-10S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-blue-900 dark:text-blue-100">Connexion WordPress</h2>
              <p className="text-sm text-blue-700/70 dark:text-blue-300/70">Paramètres de connexion à votre site WordPress avec LearnDash</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-zinc-700 dark:text-zinc-300">WordPress Base URL</label>
              <input 
                className="w-full border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 bg-white/80 dark:bg-zinc-950/70 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" 
                value={wpBaseUrl} 
                onChange={e=>setWpBaseUrl(e.target.value)} 
                placeholder="https://votre-site-wp.com" 
                disabled={saving}
              />
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">L'URL complète de votre site WordPress</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2 text-zinc-700 dark:text-zinc-300">WP Application User</label>
              <input 
                className="w-full border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 bg-white/80 dark:bg-zinc-950/70 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" 
                value={wpAppUser} 
                onChange={e=>setWpAppUser(e.target.value)}
                placeholder="nom_utilisateur"
                disabled={saving}
              />
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">Nom d'utilisateur WordPress avec les permissions nécessaires</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2 text-zinc-700 dark:text-zinc-300">WP Application Password</label>
              <input 
                type="password"
                className="w-full border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 bg-white/80 dark:bg-zinc-950/70 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" 
                value={wpAppPassword} 
                onChange={e=>setWpAppPassword(e.target.value)}
                placeholder="••••••••••••"
                disabled={saving}
              />
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">Mot de passe d'application WordPress (pas le mot de passe principal)</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2 text-zinc-700 dark:text-zinc-300">Propriétaire des sujets WordPress</label>
              <select
                className="w-full border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 bg-white/80 dark:bg-zinc-950/70 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                value={wpSubjectOwnerUserId ?? me?.id ?? ''}
                onChange={(e) => setWpOwner(Number(e.target.value))}
                disabled={saving}
              >
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.email}{u.is_superuser ? ' (admin)' : ''}</option>
                ))}
              </select>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">Les formations LearnDash seront automatiquement importées comme sujets appartenant à cet utilisateur</p>
            </div>
          </div>
        </section>

        <section className="space-y-4 rounded-3xl border border-zinc-200/70 bg-white/95 p-8 shadow-lg dark:border-zinc-800 dark:bg-zinc-900/90">
          <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">Test de connexion</h3>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            Vérifiez que la connexion à votre site WordPress fonctionne correctement
          </p>
          
          <button 
            className="rounded-full border border-blue-200/70 bg-blue-50 px-5 py-2.5 text-sm font-semibold text-blue-700 transition hover:bg-blue-100 dark:border-blue-900/60 dark:bg-blue-900/30 dark:text-blue-200 dark:hover:bg-blue-900/50" 
            type="button" 
            onClick={testCourses}
          >
            🧪 Tester la connexion LearnDash
          </button>
          
          {testError && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700 dark:bg-red-950/40 dark:border-red-900/60 dark:text-red-200">
              {testError}
            </div>
          )}
          {testMessage && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-900/60 dark:text-emerald-200">
              {testMessage}
            </div>
          )}
        </section>

        <section className="space-y-4 rounded-3xl border border-emerald-100/70 bg-white/90 p-8 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700 dark:bg-red-950/40 dark:border-red-900/60 dark:text-red-200">
              {error}
            </div>
          )}
          {message && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-900/60 dark:text-emerald-200">
              {message}
            </div>
          )}
          
          <button 
            className="w-full rounded-full bg-emerald-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60" 
            type="submit"
            disabled={saving}
          >
            {saving ? 'Enregistrement...' : '💾 Sauvegarder la configuration WordPress'}
          </button>
        </section>
      </form>
    </ConfigPageLayout>
  )
}
